import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { createRef, useRef } from 'react';
import styles from './ButtonGridItem.module.scss';
import { setupTransitionInTimeline } from './ButtonGridItem.transitions';
import type { ButtonGridItemProps, ButtonGridItemRef } from './ButtonGridItem.data';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../../atoms/heading/Heading';
import PrimaryLink from '../../../atoms/primary-link/PrimaryLink';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { PrimaryLinkRef } from '../../../atoms/primary-link/PrimaryLink.data';

export default function ButtonGridItem({
  blok,
  ref,
  ...props
}: ButtonGridItemProps & { ref?: Ref<ButtonGridItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const labelRef = useRef<HeadingRef>(null);
  const buttonsRef = useRef(blok.button.map(() => createRef<PrimaryLinkRef>()));

  useTransitionController(() => ({
    ref: elementRef,
    refs: {
      elementRef,
      labelRef,
      buttonsRef,
    },
    setupTransitionInTimeline,
  }));

  return (
    <div
      ref={elementRef}
      className={classNames(styles.buttonGridItem, props.className)}
      {...sbEditable(blok)}
    >
      <Heading
        ref={labelRef}
        as="h5"
        size="heading-05"
        transitionInType="splitWordsSlideInUp"
        className={styles.label}
        disableTriggerInOnScroll
      >
        {blok.label}
      </Heading>
      {blok.button.map((button, index) => (
        <PrimaryLink
          ref={buttonsRef.current[index]}
          key={button._uid}
          textVariant="body-standard"
          className={classNames(styles.link)}
          link={{ href: button?.link?.url || button?.link?.cached_url }}
          label={button.title}
          disableTriggerInOnScroll
          transitionInType="splitLineRotateIn"
          target={button?.link?.target}
        />
      ))}
    </div>
  );
}
